import './CustomerDetails.css';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';
import EditCustomer from './modals/EditCustomer';
import { AppContext } from '../../services/AppContext';
import { ModalContext } from '../../modal/ModalContext';
import InviteReceived from './modals/InvitationReceived';
import ReduceSubscription from './modals/ReduceSubscription';
import React, { useState, useContext, useEffect } from 'react';
import MultiSelector from '../../components/global/MultiSelector';
import SubscriptionEditor from '../subscriptions/SubscriptionEditor';
import { DeleteConfirmationModal } from '../../modal/global/DeleteConfirmationModal';

const CustomerPage = () => {
    const [customerData, setCustomerData] = useState();
    const [tagData, setTagData] = useState([]);
    const { showModal } = useContext(ModalContext);
    const { apiClient, user, notify } = useContext(AppContext);
    const administrativeRoles = ['Admin', 'Manager'];
    const apiUrl = window.env.REACT_APP_API_URL;
    const { t } = useTranslation();
    const location = useLocation();
    const { id: routeId } = useParams();

    const fetchTags = async () => {
        if (user.role !== 'Admin') return;

        const data = await apiClient.getTags();

        if (!data.hasErrors) {
            setTagData(data);
        }
    };

    const timeAgo = (timestamp) => {
        const localTime = new Date(timestamp).getTime();
        const now = new Date().getTime();
        const diff = Math.floor((now - localTime) / 1000);

        if (diff < 60) return t('pages.customers.timeAgo.seconds', { seconds: diff });
        if (diff < 3600) return t('pages.customers.timeAgo.minutes', { minutes: Math.floor(diff / 60) });
        if (diff < 86400) return t('pages.customers.timeAgo.hours', { hours: Math.floor(diff / 3600) });
        return t('pages.customers.timeAgo.days', { days: Math.floor(diff / 86400) });
    };

    const fetchData = async () => {
        let id = routeId;
        if (!id) {
            if (user.role !== 'Customer') return;
            id = user.id;
        }
        const data = await apiClient.getCustomer(id);
        if (data.hasErrors) {
            return;
        }
        setCustomerData(data);
        if (data.invitation) {
            await showModal(<InviteReceived invitation={data.invitation} />);
        }
    };

    useEffect(() => {
        fetchData();
        fetchTags();
    }, []);

    const handleEditCustomer = async () => {
        const updated = await showModal(<EditCustomer customerDto={customerData} />);
        if (updated) fetchData();
    };

    const handleDelete = async () => {
        const shouldDelete = await showModal(<DeleteConfirmationModal subject={customerData.name} />);
        if (shouldDelete) {
            await apiClient.removeCustomer(customerData.id);
            fetchData();
            notify(t('pages.customers.notifications.customerDeleted'), 'success');
        }
    };

    const handleEditSubscription = async (subscription) => {
        const updated = await showModal(<SubscriptionEditor subscription={subscription} />);
        if (updated) fetchData();
    };

    const handleReduceSubscription = async (subscription) => {
        const updated = await showModal(<ReduceSubscription subscriptionDto={subscription} />);
        if (updated) fetchData();
    };

    const handleDeleteSubscription = async (subscription) => {
        const shouldDelete = await showModal(<DeleteConfirmationModal subject={`${subscription.owner}'s subscription`} />);
        if (shouldDelete) {
            await apiClient.deleteSubscription(subscription.id);
            fetchData();
            notify(t('pages.customers.notifications.subscriptionDeleted'), 'success');
        }
    };

    const handleDeleteSpentClass = async (subscription, spentClass) => {
        const shouldDelete = await showModal(<DeleteConfirmationModal subject={`${spentClass.managerName}'s scan`} />);
        if (shouldDelete) {
            await apiClient.cancelSpentClass({
                subscriptionId: subscription.id,
                spentClassId: spentClass.id
            });
            fetchData();
            notify(t('pages.customers.notifications.scanRemoved'), 'success');
        }
    };

    const handleAddSubscription = async () => {
        const created = await showModal(<SubscriptionEditor owner={customerData} />);
        if (created) fetchData();
    };

    return (
        <div>
            {customerData && (
                <div>
                    <div className="customer-profile">
                        <div className="profile-card">
                            <div className="container-circle">
                                {customerData.profilePictureUrl ? (
                                    <img src={`${apiUrl}${customerData.profilePictureUrl}`} alt={t('pages.customers.profilePictureAlt')} />
                                ) : (
                                    <div className="placeholder-picture">{t('pages.customers.noImage')}</div>
                                )}
                            </div>
                            <div className="profile-info">
                                {administrativeRoles.includes(user.role) && (
                                    <div className="info-field">
                                        <span className="field-title">{t('pages.customers.fields.username')}:</span>
                                        <span className="field-value">{customerData.username || t('pages.customers.fields.notAvailable')}</span>
                                    </div>
                                )}
                                {administrativeRoles.includes(user.role) && customerData.notes && (
                                    <div className="info-field">
                                        <span className="field-title">{t('pages.customers.fields.notes')}:</span>
                                        <span className="field-value">{customerData.notes}</span>
                                    </div>
                                )}
                                <div className="info-field">
                                    <span className="field-title">{t('pages.customers.fields.name')}:</span>
                                    <span className="field-value">{customerData.name || t('pages.customers.fields.notAvailable')}</span>
                                </div>

                                {
                                    customerData.phone && <div className="info-field">
                                        <span className="field-title">{t('pages.customers.fields.phone')}:</span>
                                        <span className="field-value">{customerData.phone}</span>
                                    </div>
                                }

                            </div>

                            {user.role === 'Admin' && (
                                <div className="profile-actions">
                                    <button className="background-warning width-100p" onClick={handleEditCustomer}>
                                        {t('pages.customers.actions.edit')}
                                    </button>
                                    <button className="background-danger width-100p mg-l-1" onClick={handleDelete}>
                                        {t('pages.customers.actions.delete')}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Active Subscriptions */}
                    {administrativeRoles.includes(user.role) &&
                        customerData.activeSubscriptions.map((activeSubscription) => (
                            <div key={activeSubscription.id}>
                                <div className="customer-profile mg-t-2">
                                    <div className="profile-card">
                                        <div className="subscription-info">
                                            <h3 className="profile-title">
                                                <a href={`/subscription/${activeSubscription.id}`}>
                                                    {t('pages.customers.activeSubscription')}
                                                </a>
                                            </h3>
                                            <div className="container-square">
                                                {activeSubscription.qrUrl ? (
                                                    <img src={activeSubscription.qrUrl} alt={t('pages.customers.qrCodeAlt')} />
                                                ) : (
                                                    <div className="placeholder-picture">{t('pages.customers.noImage')}</div>
                                                )}
                                            </div>
                                            <div className="info-field">
                                                <span className="field-title">{t('pages.customers.fields.started')}:</span>
                                                <span className="field-value">
                                                    {new Date(activeSubscription?.starts).toISOString().split('T')[0] || t('common.notAvailable')}
                                                </span>
                                            </div>
                                            <div className="info-field">
                                                <span className="field-title">{t('pages.customers.fields.expires')}:</span>
                                                <span className="field-value">
                                                    {new Date(activeSubscription?.expires).toISOString().split('T')[0] || t('common.notAvailable')}
                                                </span>
                                            </div>
                                            <div className="info-field">
                                                <span className="field-title">{t('pages.customers.fields.total')}:</span>
                                                <span className="field-value">{activeSubscription?.total || t('pages.customers.fields.notAvailable')}</span>
                                            </div>
                                            <div className="info-field">
                                                <span className="field-title">{t('pages.customers.fields.left')}:</span>
                                                <span className="field-value">{activeSubscription?.classesLeft || t('common.notAvailable')}</span>
                                            </div>
                                            <div className="info-field">
                                                <span className="field-title">{t('pages.customers.fields.cost')}:</span>
                                                <span className="field-value">
                                                    {`${activeSubscription?.cost} ${activeSubscription?.company.defaultCurrency.symbol}` || t('pages.customers.fields.notAvailable')}
                                                </span>
                                            </div>
                                            {
                                                activeSubscription.discount > 0 && (
                                                    <div className="info-field">
                                                        <span className="field-title">{t('common.discount')}:</span>
                                                        <span className="field-value">
                                                            {`${Math.round(activeSubscription.discount * 100)}% (${activeSubscription.cost * activeSubscription.discount} ${activeSubscription?.company.defaultCurrency.symbol})`}
                                                        </span>
                                                    </div>
                                                )
                                            }
                                            {
                                                activeSubscription.managersShare > 0 && (
                                                    <div className="info-field">
                                                        <span className="field-title">{t('common.managers_share')}:</span>
                                                        <span className="field-value">
                                                            {`${activeSubscription.managersShare}%`}
                                                        </span>
                                                    </div>
                                                )
                                            }
                                            {activeSubscription.tags.length > 0 && (
                                                <div className="mg-t-1">
                                                    <MultiSelector
                                                        options={tagData}
                                                        selection={activeSubscription.tags.map((t) => t.id)}
                                                        onChange={() => { }}
                                                        isReadOnly={true}
                                                    />
                                                </div>
                                            )}
                                        </div>

                                        {user.role === 'Admin' && (
                                            <div className="profile-actions">
                                                <button className="width-100p margin" onClick={() => handleReduceSubscription(activeSubscription)}>
                                                    {t('pages.customers.actions.spend')}
                                                </button>
                                                <button className="background-warning width-100p mg-l-1" onClick={() => handleEditSubscription(activeSubscription)}>
                                                    {t('pages.customers.actions.edit')}
                                                </button>
                                                <button className="background-danger width-100p mg-l-1" onClick={() => handleDeleteSubscription(activeSubscription)}>
                                                    {t('pages.customers.actions.delete')}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Last Spent Class */}
                                {activeSubscription?.lastSpentClass && (
                                    <div className="last-class-extension">
                                        <span className="field-value">
                                        {
                                            t('pages.customers.lastUsed', { time: timeAgo(activeSubscription.lastSpentClass.timestamp) })
                                        }
                                        </span>
                                        <span className="field-value">•</span>
                                        <span className="field-value">{activeSubscription.lastSpentClass.managerName}</span>
                                        {user.role === 'Admin' && (
                                            <button
                                                style={{
                                                    padding: '0px',
                                                    margin: '1px 4px',
                                                    padding: '1px 2px',
                                                }}
                                                onClick={() => handleDeleteSpentClass(activeSubscription, activeSubscription.lastSpentClass)}
                                            >
                                                {t('pages.customers.actions.remove')}
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                        ))}

                    {/* Add Subscription */}
                    {customerData.activeSubscriptions.length === 0 && user.role === 'Admin' && (
                        <div className="customer-profile mg-t-2">
                            <button onClick={handleAddSubscription}>{t('pages.customers.actions.addSubscription')}</button>
                        </div>
                    )}

                    {user.role === 'Customer' && customerData.activeSubscriptions.map(activeSubscription => (
                        <div key={activeSubscription.id} className='subscription-ticket'>
                            <div className='ticket-container'>
                                {/* Left Side - QR Code */}
                                <div className='qr-section'>
                                    {activeSubscription.qrUrl && (
                                        <img src={activeSubscription.qrUrl} alt='QR Code' className='qr-image' />
                                    )}
                                </div>

                                {/* Right Side - Subscription Info */}
                                <div className='info-section'>
                                    {/* Company Info */}
                                    <div className='company-info'>
                                        <h2 className='company-name'>
                                            <a href={`/subscription/${activeSubscription.id}`}>
                                                {activeSubscription.company.displayName || t('common.companyName')}
                                            </a>
                                        </h2>
                                        {activeSubscription.company.logoUrl && (
                                            <div className='container-circle-small'><img src={`${apiUrl}${activeSubscription.company.logoUrl}`} alt='Company Logo' className='company-logo' /></div>
                                        )}
                                    </div>

                                    {/* Subscription Details */}
                                    <div className='details'>
                                        <div className='info-field'><span className='field-title'>{t('pages.customers.fields.started')}:</span> <span className='field-value'>{new Date(activeSubscription?.starts).toISOString().split('T')[0] || t('common.notAvailable')}</span></div>
                                        <div className='info-field'><span className='field-title'>{t('pages.customers.fields.expires')}:</span> <span className='field-value'>{new Date(activeSubscription?.expires).toISOString().split('T')[0] || t('common.notAvailable')}</span></div>

                                        {
                                            activeSubscription.discount > 0 && (
                                                <div className="info-field">
                                                    <span className="field-title">{t('common.discount')}:</span>
                                                    <span className="field-value">
                                                        {`${Math.round(activeSubscription.discount * 100)}% (${activeSubscription.cost * activeSubscription.discount} ${activeSubscription?.company.defaultCurrency.symbol})`}
                                                    </span>
                                                </div>
                                            )
                                        }
                                        <div className='info-field'><span className='field-title'>{t('pages.customers.fields.left')}:</span> <span className='field-value'>{activeSubscription?.classesLeft || t('common.notAvailable')}</span></div>
                                    </div>
                                </div>
                            </div>

                            {/* Last Spent Class - The "Detached" Extension */}
                            {activeSubscription?.lastSpentClass && (
                                <div className='last-class-extension'>
                                    <span className='field-value'>
                                        {t('pages.customers.lastUsed', { time: timeAgo(activeSubscription.lastSpentClass.timestamp) })}
                                    </span>
                                    <span className='field-value'>•</span>
                                    <span className='field-value'>{activeSubscription.lastSpentClass.managerName}</span>
                                </div>
                            )}
                        </div>
                    ))}

                    {/* Last expired Subscriptions */}
                    {administrativeRoles.includes(user.role) &&
                        customerData.lastExpiredSubscription && (
                            <div>
                                <div className="customer-profile mg-t-2">
                                    <div className="profile-card">
                                        <div className="subscription-info">
                                            <h3 className="profile-title">
                                                <a href={`/subscription/${customerData.lastExpiredSubscription.id}`}>
                                                    {t('pages.customers.lastExpired')}
                                                </a>
                                            </h3>

                                            <div className="info-field">
                                                <span className="field-title">{t('pages.customers.fields.started')}:</span>
                                                <span className="field-value">
                                                    {new Date(customerData.lastExpiredSubscription?.starts).toISOString().split('T')[0] || t('pages.customers.fields.notAvailable')}
                                                </span>
                                            </div>
                                            <div className="info-field">
                                                <span className="field-title">{t('pages.customers.fields.expires')}:</span>
                                                <span className="field-value">
                                                    {new Date(customerData.lastExpiredSubscription?.expires).toISOString().split('T')[0] || t('pages.customers.fields.notAvailable')}
                                                </span>
                                            </div>
                                            <div className="info-field">
                                                <span className="field-title">{t('pages.customers.fields.total')}:</span>
                                                <span className="field-value">{customerData.lastExpiredSubscription?.total || t('pages.customers.fields.notAvailable')}</span>
                                            </div>
                                            <div className="info-field">
                                                <span className="field-title">{t('pages.customers.fields.left')}:</span>
                                                <span className="field-value">{customerData.lastExpiredSubscription?.classesLeft || t('pages.customers.fields.notAvailable')}</span>
                                            </div>
                                            <div className="info-field">
                                                <span className="field-title">{t('pages.customers.fields.cost')}:</span>
                                                <span className="field-value">
                                                    {`${customerData.lastExpiredSubscription?.cost} ${customerData.lastExpiredSubscription?.company.defaultCurrency.symbol}` || t('pages.customers.fields.notAvailable')}
                                                </span>
                                            </div>
                                            {
                                                customerData.lastExpiredSubscription.discount > 0 && (
                                                    <div className="info-field">
                                                        <span className="field-title">{t('common.discount')}:</span>
                                                        <span className="field-value">
                                                            {`${Math.round(customerData.lastExpiredSubscription.discount * 100)}% (${customerData.lastExpiredSubscription.cost * customerData.lastExpiredSubscription.discount} ${customerData.lastExpiredSubscription?.company.defaultCurrency.symbol})`}
                                                        </span>
                                                    </div>
                                                )
                                            }
                                            {customerData.lastExpiredSubscription.tags.length > 0 && (
                                                <div className="mg-t-1">
                                                    <MultiSelector
                                                        options={tagData}
                                                        selection={customerData.lastExpiredSubscription.tags.map((t) => t.id)}
                                                        onChange={() => { }}
                                                        isReadOnly={true}
                                                    />
                                                </div>
                                            )}
                                            {user.role === 'Admin' && (
                                                <div className="profile-actions">
                                                    <button 
                                                        className="background-warning width-100p mg-l-1" 
                                                        onClick={() => handleEditSubscription(customerData.lastExpiredSubscription)}
                                                    >
                                                        {t('pages.customers.actions.edit')}
                                                    </button>
                                                    <button 
                                                        className="background-danger width-100p mg-l-1" 
                                                        onClick={() => handleDeleteSubscription(customerData.lastExpiredSubscription)}
                                                    >
                                                        {t('pages.customers.actions.delete')}
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                </div>
            )}
        </div>
    );
};

export default CustomerPage;